import { useEffect, useState } from 'react';
import api from '../services/api';

export interface Department {
  id: string;
  name: string;
  code: string;
}

// ── Module-scoped shared cache ────────────────────────────────────────────────
// One fetch is shared across every consumer (ServiceItemForm renders once per
// service item, so per-instance fetching would multiply requests). Subscribers
// are notified on refresh so live dropdowns update without a remount.
let _cache: Department[] | null = null;
let _inflight: Promise<Department[]> | null = null;
const _subscribers = new Set<(d: Department[]) => void>();

async function fetchDepartments(): Promise<Department[]> {
  if (_inflight) return _inflight;
  _inflight = api
    .get<{ departments: Department[] }>('/departments')
    .then(res => {
      _cache = res.data.departments ?? [];
      _inflight = null;
      _subscribers.forEach(fn => fn(_cache!));
      return _cache;
    })
    .catch(err => {
      _inflight = null;
      throw err;
    });
  return _inflight;
}

/** Force a refetch and notify all mounted consumers (call after adding a department). */
export function invalidateDepartments(): void {
  _cache = null;
  fetchDepartments().catch(() => { /* consumers keep their last list */ });
}

export function useDepartments() {
  const [departments, setDepartments] = useState<Department[]>(_cache ?? []);
  const [loading, setLoading] = useState(_cache === null);

  useEffect(() => {
    const onUpdate = (d: Department[]) => setDepartments(d);
    _subscribers.add(onUpdate);

    if (_cache) {
      setDepartments(_cache);
      setLoading(false);
    } else {
      fetchDepartments()
        .then(() => setLoading(false))
        .catch(() => setLoading(false));
    }

    return () => { _subscribers.delete(onUpdate); };
  }, []);

  return {
    departments,
    names: departments.map(d => d.name),
    loading,
    refresh: invalidateDepartments,
  };
}
